import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Trophy, UserPlus, TrendingUp, Users, Calendar, Search, Lock, Target } from 'lucide-react';
import { RootState, AppDispatch } from '../store';
import { fetchLeaderboard } from '../store/HallOfFameSlice';

export const HallOfFame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { leaderboardData } = useSelector((state: RootState) => state.hallOfFame);

  useEffect(() => {
    dispatch(fetchLeaderboard({ tier: 'All', page: 0, size: 50 }));
  }, [dispatch]);

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-theme-primary">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy size={48} />
              <h1 className="text-5xl font-bold">The Stock Hall of Fame</h1>
            </div>
            <p className="text-2xl mb-6 font-semibold">
              Prove Your Stock Picking Skills. Learn from the Best. 🏆
            </p>
            <p className="text-lg opacity-90 max-w-2xl mx-auto mb-8">
              Lock in your 5 best stock picks for {currentYear}. Track your performance publicly.
              Compare with other investors. Build your reputation. Your predictions matter—show the world what you've got!
            </p>

            {/* Stats Row */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Hidden Active Investors card - may discourage new users if count is low */}
              {/* <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20 hover:bg-white/15 transition-all">
                <Users size={32} className="mx-auto mb-2" />
                <div className="text-3xl font-bold">
                  {leaderboardData?.participants.length || 0}
                </div>
                <div className="text-sm opacity-90 mt-1">Active Investors</div>
              </div> */}

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20 hover:bg-white/15 transition-all">
                <TrendingUp size={32} className="mx-auto mb-2" />
                <div className="text-3xl font-bold">
                  {leaderboardData?.communityStats.averageROI.toFixed(2)}%
                </div>
                <div className="text-sm opacity-90 mt-1">Community Average</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20 hover:bg-white/15 transition-all">
                <Trophy size={32} className="mx-auto mb-2" />
                <div className="text-3xl font-bold">
                  {leaderboardData?.participants.filter((p) => p.tier === 'S').length || 0}
                </div>
                <div className="text-sm opacity-90 mt-1">Top 10% Elite</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20 hover:bg-white/15 transition-all">
                <Calendar size={32} className="mx-auto mb-2" />
                <div className="text-3xl font-bold">{currentYear}</div>
                <div className="text-sm opacity-90 mt-1">Current Season</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Primary CTAs - Clean Cards */}
        <div className="mb-16 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/hall-of-fame/submit')}
            className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-blue-200 dark:border-blue-900 hover:border-blue-500 dark:hover:border-blue-500"
          >
            <div className="relative z-10 flex flex-col items-center gap-4 text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <UserPlus size={40} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Submit Your 5 Picks</h3>
                <p className="text-gray-600 dark:text-gray-400">Put your reputation on the line. Show everyone your best ideas.</p>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent dark:from-blue-900/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>

          <button
            onClick={() => navigate('/hall-of-fame/leaderboard')}
            className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500"
          >
            <div className="relative z-10 flex flex-col items-center gap-4 text-center">
              <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Trophy size={40} className="text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">View Leaderboard</h3>
                <p className="text-gray-600 dark:text-gray-400">Learn from top performers. See who's winning and who's not.</p>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-transparent dark:from-yellow-900/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>
        </div>

        {/* How It Works */}
        <div className="mb-16 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-theme-primary mb-8 text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="w-14 h-14 bg-blue-500 text-white rounded-xl flex items-center justify-center font-bold text-2xl mb-4">
                  1
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Target size={24} className="text-blue-500" />
                  <h3 className="font-bold text-theme-primary text-xl">Pick Your Best 5</h3>
                </div>
                <p className="text-theme-muted">
                  Choose your top stock ideas. Equal-weighted $10,000 portfolio. These are your predictions for the year.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="w-14 h-14 bg-yellow-500 text-white rounded-xl flex items-center justify-center font-bold text-2xl mb-4">
                  2
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Lock size={24} className="text-yellow-500" />
                  <h3 className="font-bold text-theme-primary text-xl">Lock It In Forever 🔒</h3>
                </div>
                <p className="text-theme-muted">
                  Once submitted, your picks are permanent. No edits. No excuses. Full transparency and accountability.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="w-14 h-14 bg-green-500 text-white rounded-xl flex items-center justify-center font-bold text-2xl mb-4">
                  3
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Trophy size={24} className="text-green-500" />
                  <h3 className="font-bold text-theme-primary text-xl">Build Your Reputation</h3>
                </div>
                <p className="text-theme-muted">
                  Get ranked daily. Learn from top performers. Share your results. Prove you can beat the crowd.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Value Props */}
        <div className="mb-16 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-theme-primary mb-8 text-center">Why Join?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="text-green-600 dark:text-green-400" size={24} />
              </div>
              <h3 className="font-bold text-theme-primary mb-2 text-lg">Learn from Winners</h3>
              <p className="text-theme-muted text-sm">
                See what top investors are picking. Study their strategies. Understand what works.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-purple-600 dark:text-purple-400" size={24} />
              </div>
              <h3 className="font-bold text-theme-primary mb-2 text-lg">Public Accountability</h3>
              <p className="text-theme-muted text-sm">
                Your track record is visible. Build credibility. Earn respect. No more empty claims.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mb-4">
                <Trophy className="text-yellow-600 dark:text-yellow-400" size={24} />
              </div>
              <h3 className="font-bold text-theme-primary mb-2 text-lg">Prove Your Skills</h3>
              <p className="text-theme-muted text-sm">
                Show the world you can pick winners. Build your reputation. Get recognized.
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-theme-primary mb-3">Ready to Prove Yourself?</h2>
            <p className="text-lg text-theme-muted max-w-2xl mx-auto">
              Track your performance publicly. Lock in your picks. Build your reputation. Show what you're made of.
            </p>
          </div>
          <button
            onClick={() => navigate('/hall-of-fame/submit')}
            className="inline-flex items-center gap-3 px-12 py-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-xl rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <UserPlus size={28} />
            Submit Your 5 Picks Now
          </button>
          <p className="mt-4 text-sm text-theme-muted">
            Takes 2 minutes. Permanent record. Daily rankings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HallOfFame;

